// The dsh skin: the slot contract rendered the way the deepseek harness
// renders — one 24px inline row per item (`[glyph] Label · subject`), label
// and subject the same size one gray step apart, everything collapsed by
// default behind a whole-row toggle, a flat column with no turn chrome.
//
// This file is the whole of what makes these twelve components a *skin*. It
// adds no styling and no behaviour; it maps slot names to the elements that
// already exist, so that the composition can ask for "prose" without knowing
// that `std-dsh-turn-prose` is what answers.
//
// The `Implements` assertions below are the contract's teeth. Each one fails
// to compile if a component's props stop accepting what the contract promises
// for its slot — which is how a slot-props change lands as a build error here
// rather than as a blank row in a review card.

import { TurnSlots } from "../thread/slots";
import { DshTurnAsk } from "./ask";
import { DshTurnAttachment } from "./attachment";
import { DshTurnBar } from "./bar";
import { DshKvGrid } from "./kv-grid";
import { DshTurnNote } from "./note";
import { DshTurnPlan } from "./plan";
import { DshTurnPrompt } from "./prompt";
import { DshTurnProse } from "./prose";
import { DshTurnResult } from "./result";
import { DshTermBlock } from "./term-block";
import { DshTurnThought } from "./thought";
import { DshTurnTool } from "./tool";

// Compile-time only: nothing reads these, and removing one removes a guard.
type _Prompt = TurnSlots.Implements<TurnSlots.Props["prompt"], DshTurnPrompt.Props>;
type _Prose = TurnSlots.Implements<TurnSlots.Props["prose"], DshTurnProse.Props>;
type _Thought = TurnSlots.Implements<TurnSlots.Props["thought"], DshTurnThought.Props>;
type _Tool = TurnSlots.Implements<TurnSlots.Props["tool"], DshTurnTool.Props>;
type _Ask = TurnSlots.Implements<TurnSlots.Props["ask"], DshTurnAsk.Props>;
type _Plan = TurnSlots.Implements<TurnSlots.Props["plan"], DshTurnPlan.Props>;
type _Attachment = TurnSlots.Implements<TurnSlots.Props["attachment"], DshTurnAttachment.Props>;
type _Note = TurnSlots.Implements<TurnSlots.Props["note"], DshTurnNote.Props>;
type _Result = TurnSlots.Implements<TurnSlots.Props["result"], DshTurnResult.Props>;
type _Bar = TurnSlots.Implements<TurnSlots.Props["bar"], DshTurnBar.Props>;
type _TermBlock = TurnSlots.Implements<TurnSlots.Props["term-block"], DshTermBlock.Props>;
type _KvGrid = TurnSlots.Implements<TurnSlots.Props["kv-grid"], DshKvGrid.Props>;

/** The dsh skin. `make` is exhaustive over `TurnSlots.Slot`: the default
 * arm is unreachable by construction, and TypeScript proves it through the
 * `never` binding — a slot added to the contract lands here as a compile
 * error, which is the point. */
export const DSH_SKIN: TurnSlots.Skin = {
  name: "dsh",
  make<S extends TurnSlots.Slot>(slot: S, props: TurnSlots.Props[S]): HTMLElement {
    switch (slot) {
      case "prompt":
        return DshTurnPrompt.makeWith(props as TurnSlots.Props["prompt"]);
      case "prose":
        return DshTurnProse.makeWith(props as TurnSlots.Props["prose"]);
      case "thought":
        return DshTurnThought.makeWith(props as TurnSlots.Props["thought"]);
      case "tool":
        return DshTurnTool.makeWith(props as TurnSlots.Props["tool"]);
      case "ask":
        return DshTurnAsk.makeWith(props as TurnSlots.Props["ask"]);
      case "plan":
        return DshTurnPlan.makeWith(props as TurnSlots.Props["plan"]);
      case "attachment":
        return DshTurnAttachment.makeWith(props as TurnSlots.Props["attachment"]);
      case "note":
        return DshTurnNote.makeWith(props as TurnSlots.Props["note"]);
      case "result":
        return DshTurnResult.makeWith(props as TurnSlots.Props["result"]);
      case "bar":
        return DshTurnBar.makeWith(props as TurnSlots.Props["bar"]);
      case "term-block":
        return DshTermBlock.makeWith(props as TurnSlots.Props["term-block"]);
      case "kv-grid":
        return DshKvGrid.makeWith(props as TurnSlots.Props["kv-grid"]);
      default: {
        const unreachable: never = slot;
        throw new Error(`dsh skin: unknown slot ${String(unreachable)}`);
      }
    }
  },
};
